import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Subject, Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, BusinessViewModel, BusinessService, Int32StringTuple } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';
import { ReportListComponent } from '../report-list/report-list.component';

@Component({
    selector: 'app-business-details',
    templateUrl: './business-details.component.html',
    styleUrls: ['./business-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessDetailsComponent implements OnInit, OnChanges, CanComponentDeactivate {

    @ViewChild('form', { static: true }) form: NgForm;
    @ViewChild('app-report-list', { static: true }) reportList: ReportListComponent;

    @Input() organizationId: number;

    business: BusinessViewModel;
    businessCategories$: Observable<Int32StringTuple[]>;
    organizations$: Observable<Int32StringTuple[]>;
    users$: Observable<Array<StringStringTuple>>;
    regions$: Observable<Int32StringTuple[]>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;

    id: number;
    businessForm: UntypedFormGroup;
    private onRecordSaved = new Subject<BusinessViewModel>();
    reportSaved$ = this.onRecordSaved.asObservable();

    private static emailPattern: string = "[a-zA-Z0-9._-]+[@]+[a-zA-Z0-9-]+[.]+[a-zA-Z]{2,6}";

    public isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private businessService: BusinessService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('id'));
    }

    ngOnInit(): void {
        if (!this.organizationId) {
            this.organizationId = Number.parseInt(this.route.snapshot.queryParamMap.get('organizationid'));
        }

        this.buildForm();

        this.users$ = this.reportMetadataService.getUserMetadata();
        this.businessCategories$ = this.reportMetadataService.getBusinessCategoryMetadata();
        this.organizations$ = this.reportMetadataService.getOrganizationMetadata();
        this.regions$ = this.reportMetadataService.getRegionMetadata();

        if (this.id) {
            this.businessService.getBusinessById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
        } else {
            this.isNew = true;
            this.business = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    ngOnChanges(): void {
        if (this.business) {
            this.isNew = false;
        }
        else {
            this.isNew = true;
            this.business = this.initializeEmptyObject();
        }

        this.resetForm();
    }


    dataLoaded(business: BusinessViewModel) {
        this.business = business;

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.businessForm.controls[name];
    }


    private buildForm() {
        this.businessForm = this.formBuilder.group({
            businessId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            enabled: '',
            businessCategoryId: ['', Validators.required],
            organizationId: ['', Validators.required],
            regionId: ['', Validators.required],
            description: '',
            streetAddress: ['', Validators.maxLength(200)],
            zipCode: ['', Validators.maxLength(50)],
            postalAddress: ['', Validators.maxLength(100)],
            phone: ['', Validators.maxLength(30)],
            email: ['', [Validators.maxLength(100), Validators.pattern(BusinessDetailsComponent.emailPattern)]],
            contactPerson: ['', Validators.maxLength(200)],
            siteUrl: ['', Validators.maxLength(200)],
            latitude: '',
            longitude: '',
            changedScoreLevel: '',
            latestScoreLevel: '',
            previousScoreLevel: '',
            latestApprovedReportDate: '',
            previousApprovedReportDate: '',
            ownedBy: '',
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
        });
    }

    public resetForm() {
        if (!this.business) {
            this.isNew = true;
            this.business = this.initializeEmptyObject();
        }

        if (this.isNew) {
            if (!this.business.ownedBy) {
                this.business.ownedBy = this.authService.currentUser.id;
            }

            if (!this.business.organizationId && this.organizationId) {
                this.business.organizationId = this.organizationId;
            }

        }


        this.businessForm.reset({
            businessId: this.business.businessId || '',
            name: this.business.name || '',
            enabled: this.business.enabled || '',
            businessCategoryId: this.business.businessCategoryId || '',
            organizationId: this.business.organizationId || '',
            regionId: this.business.regionId || '',
            description: this.business.description || '',
            streetAddress: this.business.streetAddress || '',
            zipCode: this.business.zipCode || '',
            postalAddress: this.business.postalAddress || '',
            phone: this.business.phone || '',
            email: this.business.email || '',
            contactPerson: this.business.contactPerson || '',
            siteUrl: this.business.siteUrl || '',
            latitude: this.business.latitude || '',
            longitude: this.business.longitude || '',
            changedScoreLevel: this.business.changedScoreLevel || '',
            latestScoreLevel: this.business.latestScoreLevel || '',
            previousScoreLevel: this.business.previousScoreLevel || '',
            latestApprovedReportDate: new DateFormatterPipe().transform(this.business.latestApprovedReportDate, false) || '',
            previousApprovedReportDate: new DateFormatterPipe().transform(this.business.previousApprovedReportDate, false) || '',
            ownedBy: this.business.ownedBy || '',
            createdBy: this.business.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.business.createdDate, true) || '',
            updatedBy: this.business.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.business.updatedDate, true) || '',
        });

        if (this.business.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.business.createdBy).pipe(map(t => t.item2));
        }
        if (this.business.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.business.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): BusinessViewModel {
        var result = <BusinessViewModel>{};
        result.enabled = true;

        return result;
    }

    private prepareSave(): BusinessViewModel {
        const formModel = this.businessForm.value;

        return {
            businessId: this.isNew ? null : formModel.businessId,
            name: formModel.name,
            enabled: formModel.enabled ? true : false,
            businessCategoryId: formModel.businessCategoryId,
            organizationId: formModel.organizationId,
            regionId: formModel.regionId,
            description: formModel.description,
            streetAddress: formModel.streetAddress,
            zipCode: formModel.zipCode,
            postalAddress: formModel.postalAddress,
            phone: formModel.phone,
            email: formModel.email,
            contactPerson: formModel.contactPerson,
            siteUrl: formModel.siteUrl,
            latitude: formModel.latitude,
            longitude: formModel.longitude,
            ownedBy: formModel.ownedBy,
        };
    }

    public save() {
        if (!this.businessForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const business = this.prepareSave();

        if (this.isNew) {
            this.businessService.createBusiness(business).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.businessService.updateBusiness(business.businessId, business).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.businessForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(
                merge(remain),
                take(1));

        } else {
            return of(true);
        }
    }

    private saveCompleted(business: BusinessViewModel) {
        this.business = business;
        this.id = business.businessId;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), business.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();

        this.onRecordSaved.next(this.business);
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeBusinesses);
    }

}
